import {
  Box,
  ButtonProps,
  Modal,
  ModalBody,
  ModalContent,
  ModalContentProps,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  useToken,
} from "@chakra-ui/react";
import { PropsWithChildren, ReactNode } from "react";
import React from "react";
import { FormattedMessage } from "react-intl";

import { hexToRgb } from "@nens/shared-utils";

import { RightArrowIcon } from "../../icons";
import { SIZES } from "../theme/consts/sizing-consts";

import { ButtonWrapper } from "./button-wrapper";
import { RanaButton } from "./rana-button";
import { RanaHeading } from "./rana-heading";

export interface ModalProps extends ModalContentProps {
  isOpen: boolean;
  bottomChild?: ReactNode;
  buttons?: (typeof RanaButton | ButtonProps)[];
  topBar?: string | ReactNode | JSX.Element;
  isCentered?: boolean;
  bodyPx?: number | string;
  onClose?: () => void;
  onClickOnCloseText?: () => void;
  onCloseText?: string;
  blurBackground?: boolean;
}

const emptyFunc = () => undefined;

const DEFAULT_MODAL_PADDING = SIZES[11];

export function RanaModal({
  topBar,
  buttons,
  bottomChild,
  isOpen,
  isCentered = true,
  onClose,
  onCloseText,
  onClickOnCloseText,
  children,
  bodyPx = DEFAULT_MODAL_PADDING,
  blurBackground = true,
  ...rest
}: PropsWithChildren<ModalProps>) {
  const modalBackdropColor = useToken("colors", "brand.modal_backdrop");
  const size8xl = useToken("sizes", "8xl");

  return (
    <Modal
      blockScrollOnMount={false} // If this is true and there are 2 Modals rendered at the same time you will not be able to scroll in the second one
      isOpen={isOpen}
      onClose={onClose ?? emptyFunc}
      isCentered={isCentered}
    >
      {blurBackground ? (
        <ModalOverlay
          backdropFilter="blur(5px)"
          bg={`rgba(${hexToRgb(modalBackdropColor)}, 0.6)`}
        />
      ) : null}
      <ModalContent
        minW="lg"
        w="fit"
        maxW={`min(calc(100% - ${DEFAULT_MODAL_PADDING}), ${size8xl})`}
        h="fit"
        maxH={`calc(100% - ${DEFAULT_MODAL_PADDING})`}
        bg="brand.grey.200"
        {...rest}
      >
        <ModalHeader
          px={DEFAULT_MODAL_PADDING}
          pt={DEFAULT_MODAL_PADDING}
          display="flex"
          alignItems="center"
        >
          <RanaHeading mr={4} type="h3">
            {topBar}
          </RanaHeading>
          <Spacer />
          {(onClose || onClickOnCloseText) && (
            <RanaButton
              flexShrink={0}
              onClick={onClose || onClickOnCloseText}
              variant="fourthiary"
              rightIcon={<RightArrowIcon />}
            >
              {onCloseText || <FormattedMessage id="button.close" defaultMessage="Close" />}
            </RanaButton>
          )}
        </ModalHeader>
        <ModalBody overflow="auto" px={DEFAULT_MODAL_PADDING} py={2} fontWeight={300}>
          {children}
        </ModalBody>
        {(buttons || bottomChild) && (
          <ModalFooter
            justifyContent="space-between"
            px={DEFAULT_MODAL_PADDING}
            pt={DEFAULT_MODAL_PADDING}
          >
            <Box>{bottomChild}</Box>
            <ButtonWrapper>
              {buttons?.map((button, idx) => {
                if (!(typeof button === "object")) return null;
                if (React.isValidElement(button)) return button;
                return <RanaButton key={idx} {...button}></RanaButton>;
              })}
            </ButtonWrapper>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
}
